import { useEffect, useState, useMemo } from 'react';
import Cookies from 'js-cookie';
import {
  FBC_PARAM,
  FBP_PARAM,
  GOOGLE_ONE_LINK,
  META_ONE_LINK,
  TIKTOK_ONE_LINK,
  FB_PIXEL_ID,
  GOOGLE_ADS_ACCOUNT_ID,
} from '../constants';
import { queryParser, detectAnalyticsSource } from '../utils';

const DEEP_LINK_SCHEME = 'family-locator-webtoapp://';

const sourceConfigs = {
  facebook: {
    oneLinkUrl: META_ONE_LINK,
    mediaSourceDefault: 'facebook',
    getCampaignKeys: () => ['utm_campaign', 'af_c', 'c', 'campaign_name'],
    getAdKeys: () => ['utm_ad', 'af_ad', 'AdName', 'ad_name'],
    getAdSetKeys: () => ['utm_content', 'af_adset', 'AdSetName', 'adset_name'],
    getAfSubs: (params) => {
      const fbc = Cookies.get(FBC_PARAM) || '';
      const fbp = Cookies.get(FBP_PARAM) || '';
      const AFUserID = Cookies.get('afUserId') || '';
      const encodedUrl = encodeURIComponent(document.location.href);

      return {
        afSub1: { keys: ['af_sub1'], defaultValue: '' },
        afSub2: {
          keys: ['af_sub2', 'fbclid'],
          defaultValue: params.fbclid || '',
        },
        afSub3: { keys: ['af_sub3'], defaultValue: FB_PIXEL_ID || '' },
        afSub4: {
          keys: ['af_sub4'],
          defaultValue: `${fbc};${fbp};${encodedUrl};${AFUserID}`,
        },
        afSub5: {
          keys: ['af_sub5'],
          defaultValue: encodeURI(window.navigator.userAgent),
        },
      };
    },
    getCampaignParams: (params) => ({
      campaignId: params.af_c_id || params.campaign_id || '',
      adsetId: params.af_adset_id || params.adset_id || '',
      adId: params.af_ad_id || params.ad_id || '',
    }),
  },
  google: {
    oneLinkUrl: GOOGLE_ONE_LINK,
    mediaSourceDefault: 'Google_Search',
    getCampaignKeys: () => ['utm_campaign', 'af_c', 'c'],
    getAdKeys: () => ['utm_ad', 'af_ad', 'AdName'],
    getAdSetKeys: () => ['utm_content', 'af_adset'],
    getAfSubs: (params) => ({
      afSub1: { keys: ['af_sub1'], defaultValue: params.af_sub1 || '' },
      afSub2: { keys: ['af_sub2'], defaultValue: params.af_sub2 || '' },
      afSub3: { keys: ['af_sub3'], defaultValue: params.af_sub3 || '' },
      afSub4: { keys: ['af_sub4'], defaultValue: GOOGLE_ADS_ACCOUNT_ID || '' },
      afSub5: {
        keys: ['af_sub5'],
        defaultValue: encodeURI(window.navigator.userAgent),
      },
    }),
    getCampaignParams: (params) => ({
      campaignId: params.af_c_id || params.campaignid || '',
      adsetId: params.af_adset_id || params.adgroupid || '',
      adId: '',
    }),
  },
  tiktok: {
    oneLinkUrl: TIKTOK_ONE_LINK,
    mediaSourceDefault: 'bytedanceglobal_int',
    getCampaignKeys: () => ['utm_campaign', 'af_c', 'campaign_name'],
    getAdKeys: () => ['utm_ad', 'af_ad', 'ad_name'],
    getAdSetKeys: () => ['utm_content', 'af_adset', 'adset_name'],
    getAfSubs: (params) => ({
      afSub1: {
        keys: ['af_sub1', 'ttclid'],
        defaultValue: params.ttclid || '',
      },
      afSub2: { keys: ['af_sub2'], defaultValue: params.campaign_id || '' },
      afSub3: { keys: ['af_sub3'], defaultValue: params.ad_id || '' },
      afSub4: { keys: ['af_sub4'], defaultValue: params.adset_id || '' },
      afSub5: {
        keys: ['af_sub5'],
        defaultValue: encodeURI(window.navigator.userAgent),
      },
    }),
    getCampaignParams: (params) => ({
      campaignId: params.campaign_id || '',
      adsetId: params.adset_id || '',
      adId: params.ad_id || '',
    }),
  },
};

export const useOneLinkGenerator = () => {
  const [oneLinkUrl, setOneLinkUrl] = useState('');
  const [isSourceInitialized, setIsSourceInitialized] = useState(false);
  const [source, setSource] = useState(null);

  const params = useMemo(() => queryParser(window.location.search), []);

  useEffect(() => {
    const detectedSource = detectAnalyticsSource(params);
    setSource(detectedSource);
  }, [params]);

  useEffect(() => {
    if (!source) return;

    if (source === 'facebook') {
      const checkCookie = setInterval(() => {
        const fbc = Cookies.get(FBC_PARAM);
        const fbp = Cookies.get(FBP_PARAM);
        if (!!fbc && !!fbp) {
          setIsSourceInitialized(true);
          clearInterval(checkCookie);
        }
      }, 500);

      return () => clearInterval(checkCookie);
    } else if (source === 'google') {
      if (params.af_sub1 || params.af_sub2 || params.af_sub3) {
        setIsSourceInitialized(true);
      } else {
        const checkGoogle = setInterval(() => {
          const currentParams = queryParser(window.location.search);
          if (
            currentParams.af_sub1 ||
            currentParams.af_sub2 ||
            currentParams.af_sub3
          ) {
            setIsSourceInitialized(true);
            clearInterval(checkGoogle);
          }
        }, 500);

        return () => clearInterval(checkGoogle);
      }
    } else if (source === 'tiktok') {
      setIsSourceInitialized(true);
    }
  }, [source, params]);

  useEffect(() => {
    if (!isSourceInitialized || !source || !window?.AF_SMART_SCRIPT) return;

    const config = sourceConfigs[source];
    const campaignParams = config.getCampaignParams(params);
    const afSubs = config.getAfSubs(params);

    const afParameters = {
      mediaSource: {
        keys: ['utm_source', 'af_pid'],
        defaultValue: config.mediaSourceDefault,
      },
      campaign: {
        keys: config.getCampaignKeys(),
        defaultValue:
          params.c || params.campaign_name || params.utm_campaign || '',
      },
      channel: {
        keys: ['AdChannel', 'utm_medium', 'af_channel'],
        defaultValue: params.utm_medium || '',
      },
      ad: {
        keys: config.getAdKeys(),
        defaultValue: params.af_ad || params.ad_name || '',
      },
      adSet: {
        keys: config.getAdSetKeys(),
        defaultValue:
          params.af_adset ||
          params.adset_name ||
          params.adgroupid ||
          params.utm_content ||
          '',
      },
      deepLinkValue: {
        keys: ['deep_link_value'],
        defaultValue: params.deep_link_value || '',
      },
      ...afSubs,
    };

    const resultLink = window.AF_SMART_SCRIPT.generateOneLinkURL({
      oneLinkURL: config.oneLinkUrl,
      afParameters,
    });

    if (!resultLink?.clickURL) {
      console.error('Failed to generate OneLink URL');
      return;
    }

    const additionalParams = [
      campaignParams.campaignId ? `af_c_id=${campaignParams.campaignId}` : '',
      campaignParams.adsetId ? `af_adset_id=${campaignParams.adsetId}` : '',
      campaignParams.adId ? `af_ad_id=${campaignParams.adId}` : '',
      `gclid=${params.gclid || params.af_sub1 || ''}`,
      `af_partner_account_id=${
        params.af_partner_account_id || GOOGLE_ADS_ACCOUNT_ID
      }`,
      `af_dp=${DEEP_LINK_SCHEME}`,
    ]
      .filter(Boolean)
      .join('&');

    setOneLinkUrl(
      `${resultLink.clickURL}${additionalParams ? `&${additionalParams}` : ''}`
    );
  }, [isSourceInitialized, source, params]);

  return oneLinkUrl;
};
